import { useOutletContext } from "react-router-dom";
import type { MeetingRunContext } from "../MeetingRunContext";
import {
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
import { TextAreaAuto } from "@repo/ui/components/Base/TextAreaAuto";
import { SelectPresenter } from "@/components/common/SelectPresenter";
import { CollapsiblePanel } from "../CollapsiblePanel";
import { getSectionById } from "./sections";
import type { MeetingRunSectionProps } from "./meetingRunSectionProps";
import {
  agendaItemRepo,
  type AgendaItemDto,
} from "@/repositories/agendaItemRepo";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";

export const MeetingRunPresentSection = ({
  id,
  idSuffix,
  className,
  open,
  onToggle,
}: MeetingRunSectionProps) => {
  const {
    tenantId,
    mutationSystem,
    agendaItem: _item,
  } = useOutletContext() as MeetingRunContext;

  const mutationKey = "agendaItem:" + _item!.id;

  const agendaItemMutation: Mutator<AgendaItemDto> = useFutureCallback(
    async (id, data) => {
      await agendaItemRepo.update(tenantId, id, data);
    }
  );
  const agendaItem = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _item!
  );

  return (
    <CollapsiblePanel
      id={id}
      idSuffix={idSuffix}
      title={getSectionById("present").title}
      className={className}
      open={open}
      onToggle={onToggle}
    >
      <TextAreaAuto
        id="description"
        label="Description"
        className="mb-2"
        value={agendaItem.description ?? ""}
        onChange={(evt) => {
          mutationSystem.debounce(
            mutationKey,
            agendaItemMutation,
            { description: evt.target.value },
            agendaItem.id
          );
        }}
      />

      <h1 className="mt-2 text-2xl font-bold">Script</h1>
      <TextAreaAuto
        id="script"
        value={agendaItem.script ?? ""}
        onChange={(evt) => {
          mutationSystem.debounce(
            mutationKey,
            agendaItemMutation,
            { script: evt.target.value },
            agendaItem.id
          );
        }}
      />

      <SelectPresenter
        label="Presenter"
        value={agendaItem.presenter}
        onChange={(presenter) => {
          mutationSystem.debounce(
            mutationKey,
            agendaItemMutation,
            { presenter },
            agendaItem.id
          );
        }}
        team={agendaItem.teamId}
      />

      <TextAreaAuto
        id="comment"
        label="Staff Comment"
        className="mb-2"
        value={agendaItem.comment ?? ""}
        onChange={(evt) => {
          mutationSystem.debounce(
            mutationKey,
            agendaItemMutation,
            { comment: evt.target.value },
            agendaItem.id
          );
        }}
      />
    </CollapsiblePanel>
  );
};
